/**
 * NailDemoContent - EXACT replica of phygital.systems/nail landing page
 * Used as demo tab content for Nail pilot invites
 * All text and images are editable by super admin
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useAdmin, EditableText, EditableImage } from "@/components/AdminContentEditor";
import {
  ArrowRight,
  Check,
  X,
  Clock,
  AlertCircle,
  Image as ImageIcon,
  Eye,
  Palette,
  Heart,
  Users,
  Sparkles
} from "lucide-react";

export default function NailDemoContent({ onAccept, portfolioImages = [] }) {
  const { editMode } = useAdmin();

  // Features from exact PDF - "Built for Independent Nail Techs"
  const features = [
    {
      title: "Inspo Before They Arrive",
      desc: "Clients share Pinterest boards, photos, and design ideas when they book. Not when they sit down."
    },
    {
      title: "Accurate Time Estimates",
      desc: "Know if it's a simple color or intricate nail art before scheduling. No more rushed appointments."
    },
    {
      title: "Color & Product Prep",
      desc: "See their preferred shades and know what to pull from your kit before they walk in."
    },
    {
      title: "Allergy & Sensitivity Alerts",
      desc: "Know about sensitivities to acetone, gel allergies, or cuticle sensitivity ahead of time."
    },
    {
      title: "Remember Every Client",
      desc: "Shape preference, length, favorite colors. All saved and ready for repeat visits."
    },
    {
      title: "Accurate Pricing Upfront",
      desc: "Quote correctly based on design complexity. No awkward price surprises at checkout."
    }
  ];

  // "Sound Familiar?" problems from exact PDF
  const problems = [
    { problem: "Client shows inspo at the chair", solution: "See their vision before they arrive" },
    { problem: "Rushed because design takes longer", solution: "Know complexity when booking" },
    { problem: "Wrong color/shade assumptions", solution: "Client confirms exact preferences" },
    { problem: "Forgetting regular client details", solution: "Preferences saved & synced" }
  ];

  // Services from exact PDF
  const services = [
    { name: "Classic Mani", duration: "30 min", price: "$35+" },
    { name: "Gel Manicure", duration: "45 min", price: "$55+" },
    { name: "Full Set Acrylics", duration: "1.5 hrs", price: "$85+" },
    { name: "Nail Art", duration: "30 min+", price: "$15+" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-rose-100 text-rose-700">
                Nail Tech Pilot by Phygital
              </Badge>
              <EditableText
                sectionId="nail-demo-hero"
                field="title"
                defaultValue="See Their Vision Before They Sit Down"
                className="text-4xl sm:text-5xl font-bold mb-4"
                as="h1"
              />
              <EditableText
                sectionId="nail-demo-hero"
                field="subtitle"
                defaultValue="Clients share their inspo, shape, and design preferences when they book. Not when they're already in your chair."
                className="text-gray-600 text-lg mb-6"
                as="p"
              />
              
              <p className="text-sm text-gray-500 mb-4">Works with any booker</p>
              <div className="flex gap-3 mb-6">
                <Badge variant="outline">Solo Nail Techs</Badge>
                <Badge variant="outline">Salons & Studios</Badge>
              </div>

              <Button onClick={onAccept} className="bg-gradient-to-r from-pink-400 to-rose-500 text-white rounded-full px-8 h-12">
                See How It Works
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div>
              {/* Feature tags from PDF */}
              <div className="flex flex-wrap gap-2 mb-6">
                {["Inspo before booking", "Color preferences", "Design approval flow"].map(tag => (
                  <Badge key={tag} variant="secondary" className="bg-white border">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design, Inspo, and Prep Details - Exact from PDF */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">Design, Inspo, and Prep Details</h2>
            <p className="text-gray-600">Each client shares their vision before arriving. You see the supplies and time needed.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Client View Phone Mockup */}
            <Card className="border-2">
              <CardContent className="p-0">
                <div className="bg-gray-100 px-4 py-2 border-b flex items-center gap-2">
                  <span className="text-xs">9:41</span>
                  <span className="text-xs ml-auto">Client View</span>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold mb-4">Share your nail vision for your appointment</h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Nail Shape</p>
                        <EditableImage
                          sectionId="nail-demo-shapes"
                          field="shape_1"
                          defaultImage={null}
                          className="aspect-square rounded-lg overflow-hidden"
                          placeholderClassName="bg-gradient-to-br from-rose-100 to-pink-100"
                          placeholderIcon={<Palette className="w-6 h-6 text-rose-300" />}
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Length</p>
                        <EditableImage
                          sectionId="nail-demo-shapes"
                          field="length_1"
                          defaultImage={null}
                          className="aspect-square rounded-lg overflow-hidden"
                          placeholderClassName="bg-gradient-to-br from-rose-100 to-pink-100"
                          placeholderIcon={<Palette className="w-6 h-6 text-rose-300" />}
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Finish</p>
                        <EditableImage
                          sectionId="nail-demo-shapes"
                          field="finish_1"
                          defaultImage={null}
                          className="aspect-square rounded-lg overflow-hidden"
                          placeholderClassName="bg-gradient-to-br from-rose-100 to-pink-100"
                          placeholderIcon={<Palette className="w-6 h-6 text-rose-300" />}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg">
                      <span className="text-sm">French</span>
                      <Badge className="bg-rose-500">Long</Badge>
                    </div>

                    <Button variant="outline" className="w-full">
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Add inspiration
                    </Button>

                    <p className="text-xs text-green-600 text-center">✓ Preferences saved</p>
                    <p className="text-xs text-gray-500 text-center">Client shares their vision</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Provider Dashboard View */}
            <Card className="border-2">
              <CardContent className="p-0">
                <div className="bg-gray-800 text-white px-4 py-2 text-xs flex items-center">
                  <span>●●● phygital.systems/nail/dashboard</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Today's Appointments</h3>
                  </div>

                  <div className="border rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-rose-400 to-pink-400 flex items-center justify-center text-white font-bold">
                        M
                      </div>
                      <div>
                        <p className="font-semibold">Maya <span className="font-normal text-gray-500">10:00 AM</span></p>
                        <p className="text-sm text-gray-600">Gel Mani - Almond, Medium</p>
                      </div>
                    </div>

                    <div className="bg-pink-50 rounded-lg p-3 mb-3">
                      <p className="text-sm font-medium text-pink-700">Design Request:</p>
                      <p className="text-sm">Nude base with gold chrome French tips</p>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-3">
                      {["Nude gel", "Gold chrome", "Top coat"].map(item => (
                        <Badge key={item} variant="secondary" className="text-xs">{item}</Badge>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 text-amber-600 text-xs">
                      <AlertCircle className="w-3 h-3" />
                      <span>Client noted: Sensitive to acetone remover</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 text-center">You see design + supplies needed</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Built for Independent Nail Techs - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-rose-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">Built for Independent Nail Techs</h2>
            <p className="text-gray-600">Mobile, booth rent, or studio. Phygital helps you prep better and serve faster.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <Badge className="mb-3 bg-green-100 text-green-700">Ready</Badge>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sound Familiar? - Exact from PDF */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">Sound Familiar?</h2>
            <p className="text-gray-600">Common frustrations we solve</p>
          </div>

          <div className="space-y-4">
            {problems.map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 flex-1">
                  <X className="w-5 h-5 text-red-500" />
                  <span className="text-gray-700">{item.problem}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <div className="flex items-center gap-2 flex-1">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{item.solution}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Works With All Your Services - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-rose-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">Works With All Your Services</h2>
            <p className="text-gray-600">Clients share preferences for any service</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {services.map((service, i) => (
              <Card key={i} className="text-center hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <EditableImage
                    sectionId="nail-demo-services"
                    field={`service_${i}`}
                    defaultImage={null}
                    className="w-16 h-16 mx-auto mb-3 rounded-xl overflow-hidden"
                    placeholderClassName="bg-gradient-to-br from-rose-100 to-pink-100"
                    placeholderIcon={<Sparkles className="w-6 h-6 text-rose-300" />}
                  />
                  <h3 className="font-semibold text-sm">{service.name}</h3>
                  <p className="text-xs text-gray-500">{service.duration}</p>
                  <p className="text-sm font-medium text-rose-600 mt-1">{service.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Exact from PDF */}
      <section className="py-20 px-4 bg-gradient-to-r from-pink-400 to-rose-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to See Client Visions Before They Arrive?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Join the pilot program and get early access. Works with your existing booking system.
          </p>
          <Button
            onClick={onAccept}
            size="lg"
            className="bg-white text-rose-600 hover:bg-gray-100 h-14 px-10 rounded-full text-lg font-semibold"
          >
            Accept Invite
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="mt-4 text-white/70 text-sm">
            Free during pilot. No booking system changes required
          </p>
        </div>
      </section>
    </div>
  );
}
